// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2: an AI agent is PROJECT CONTEXT, never a team
// member. Nothing here reaches the solver, and no agent carries a Powers profile — Powers is a
// psychometric instrument built for humans, and putting agents on that scale would argue they are
// interchangeable with candidates, which is the model @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2 rejects.

import type { ReactNode } from "react";
import { Link } from "react-router";
import { AgentMonogram } from "./AgentMonogram";

/**
 * One agent as a compact row, in the selected list and in the instance's agent list. Takes an `action` slot
 * instead of an `onRemove` flag so the two callers stay one component: the picker passes a remove
 * control, the agent list an add/added toggle, the result panel nothing.
 *
 * `to` is the agent's own page (`/a/:id`), which is its detail view. It is optional because a
 * stored agent can outlive its genesis row: the row still renders from the saved snapshot, but
 * there is no page to open, so it must not look clickable.
 */
export function AgentRow({
  id,
  name,
  role,
  covers = [],
  to,
  action,
}: {
  id?: string;
  name: string;
  role: string;
  covers?: string[];
  to?: string;
  action?: ReactNode;
}) {
  const body = (
    <>
      <div id={id && `${id}-name`} className="text-smycles-meta font-medium text-ink transition group-hover:text-ink-accent">{name}</div>
      {/* No role in the reader's language: no empty line either. */}
      {role ? <div id={id && `${id}-role`} className="line-clamp-2 text-smycles-label leading-snug text-ink-muted">{role}</div> : null}
      {covers.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {covers.map((c) => (
            <span key={c} className="bg-ink/[0.06] px-1.5 py-0.5 text-smycles-micro text-ink-muted">
              {c}
            </span>
          ))}
        </div>
      ) : null}
    </>
  );
  return (
    // The ring is on the whole row, and a wash on the click target was tried and reverted: the button
    // spans neither the monogram nor the action's gutter, so its wash rendered as a grey rectangle
    // floating mid-row, aligned to nothing. The row is the only edge that matches the shape.
    <div
      id={id}
      className={`box-flat bg-page flex items-start gap-3 p-3 ${
        to ? "group transition hover:ring-accent-decoration/40" : ""
      }`}
    >
      <AgentMonogram name={name} />
      {to ? (
        <Link
          id={id && `${id}-link`}
          to={to}
          className="block min-w-0 flex-1 rounded text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
        >
          {body}
        </Link>
      ) : (
        <div className="min-w-0 flex-1">{body}</div>
      )}
      {action}
    </div>
  );
}
